import { Form } from "../../../../../../../shared/infrastructure";
import { OfferFormStep } from "./offer-form-step";
import { OfferSummaryStep } from "./offer-summary-step";
import { OfferT } from "../../../../../domain";
import toast from "react-hot-toast";
import { NewEntryFormSchema, OfferFormT, StepsT } from "./types";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import {
  useCreateOffer,
  useUpdateOffer,
  useZipCodesOrder,
} from "../../../../services";

type RateEntryProps = {
  onClose: () => void;
  offer?: OfferT;
};

export const RateEntry = ({
  onClose,
  offer,
}: RateEntryProps): React.ReactElement => {
  const [step, setStep] = useState<StepsT>("offer-form");
  const { mutateAsync: createSingleOffer, isLoading: createIsLoading } =
    useCreateOffer();
  const { mutateAsync: updateSingleOffer, isLoading: updateIsLoading } =
    useUpdateOffer();
  const { mutateAsync: updateZipCodesOrder, isLoading: isLoadingZipCodes } =
    useZipCodesOrder();

  const defaultValues: OfferFormT = useMemo(
    () =>
      offer
        ? {
            carrier: offer.carrier,
            order: {
              orderNumber: offer.order_number,
              movementNumber: offer.movement_number,
              loads_origin: offer.origin,
              loads_destination: offer.destination,
            },
            rate: offer.rate,
            comments: offer.comments,
            truckLocation: offer.truck_location,
            emptyTruck: offer.is_empty_truck,
            origin_zip_code: offer.origin.zip_code
              ? Number(offer.origin.zip_code)
              : undefined,
            destination_zip_code: offer.destination.zip_code
              ? Number(offer.destination.zip_code)
              : undefined,
            commission: offer.commission ?? undefined,
          }
        : {},
    [offer]
  );

  const StepComponent: React.ReactNode = useMemo(() => {
    switch (step) {
      case "offer-form":
        return <OfferFormStep setStep={setStep} />;
      case "summary":
        return (
          <OfferSummaryStep
            isLoading={createIsLoading || updateIsLoading || isLoadingZipCodes}
            setStep={setStep}
          />
        );
    }

    return <Fragment />;
  }, [createIsLoading, step, updateIsLoading, isLoadingZipCodes]);

  const onSubmitForm = useCallback(
    (formData: OfferFormT) => {
      if (
        formData.carrier &&
        formData.order?.movementNumber &&
        formData.rate !== undefined &&
        formData.commission !== undefined &&
        formData.truckLocation
      ) {
        if (
          !formData.order?.loads_origin?.zipCode ||
          !formData.order?.loads_destination?.zipCode
        ) {
          updateZipCodesOrder({
            movementNumber: formData.order.movementNumber,
            payload: {
              origin: {
                zip_code: formData.origin_zip_code?.toString() ?? "",
              },
              destination: {
                zip_code: formData.destination_zip_code?.toString() ?? "",
              },
            },
          }).catch(() => {
            toast.error("Something happen adding zip codes");
          });
        }

        const payload = {
          carrier_id: formData.carrier.carrier_id,
          rate: formData.rate,
          commission: formData.commission,
          movement_number: formData.order.movementNumber,
          truck_location_id: formData.truckLocation?.location_id,
          is_empty_truck: !!formData.emptyTruck,
          ...(formData.comments ? { comments: formData.comments ?? "" } : {}),
          offer_source: formData.offerSource?.description,
          offer_source_id: formData.offerSource?.id,
        };

        if (offer) {
          updateSingleOffer({
            type: "single",
            offerId: offer.approval_id,
            payload,
          })
            .then(() => {
              toast.success("Single offer has been updated");
              onClose();
            })
            .catch(() => {
              toast.error("Something happen, please try again later");
            });
        } else {
          createSingleOffer({
            type: "single",
            payload,
          })
            .then(() => {
              toast.success("Single offer has been updated");
              onClose();
            })
            .catch(() => {
              toast.error("Something happen, please try again later");
            });
        }
      }
    },
    [createSingleOffer, offer, onClose, updateSingleOffer, updateZipCodesOrder]
  );

  return (
    <Form
      onSubmit={onSubmitForm}
      schema={NewEntryFormSchema}
      mode="onChange"
      defaultValues={defaultValues}
    >
      <section className="w-[450px]">{StepComponent}</section>
    </Form>
  );
};
